import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { UserRole } from '@app/core/models';

export interface AnalyticsRoutePolicy {
  pageView: boolean;
  customEvents: boolean;
  replay: boolean;
}

export type AnalyticsArea = 'registration' | 'owner';

export interface AnalyticsRouteDecision {
  area: AnalyticsArea | null;
  routeTemplate: string | null;
  policy: AnalyticsRoutePolicy;
}

export const ANALYTICS_OFF: AnalyticsRoutePolicy = {
  pageView: false,
  customEvents: false,
  replay: false
};

const REGISTRATION: AnalyticsRoutePolicy = {
  pageView: true,
  customEvents: true,
  // Replay additionally requires an explicit opt-in from the safe connection view.
  replay: false
};

const OWNER: AnalyticsRoutePolicy = {
  pageView: true,
  customEvents: true,
  replay: false
};

const REGISTRATION_ROUTES = new Set([
  '/register', '/register/pms', '/billing/success', '/billing/canceled'
]);
const REGISTRATION_REPLAY_EXCLUDED = new Set(['/register/pms', '/billing/success', '/billing/canceled']);

const OWNER_ROUTES = new Set([
  '/dashboard', '/properties', '/addons', '/signals', '/market-overview',
  '/performance', '/email-queue', '/action-queue', '/manual-actions', '/help',
  '/bookings', '/messages', '/guest-messaging/reviews/:reviewId', '/ops',
  '/weekly-review', '/certifications', '/certifications/review', '/compliance',
  '/settings', '/settings/integrations/pms',
  '/settings/integrations/pms/connect/ownerrez',
  '/settings/integrations/pms/connect/beds24',
  '/settings/integrations/pms/connections/:connectionId',
  '/properties/:propertyId/ai-guest-messaging/knowledge',
  '/properties/:propertyId/pms', '/properties/:propertyId/pms/sync',
  '/properties/:propertyId/pms/events', '/pms/write-approvals',
  '/pms/write-approvals/:writeRequestId', '/imports', '/imports/:orderId',
  '/imports/:orderId/reports'
]);

// Edit this table to suppress one route without changing feature components.
// A route's `data.analytics` can also override these three flags. Explicitly
// excluded routes below always remain off, even if route data enables a flag.
export const ROUTE_ANALYTICS_OVERRIDES: Readonly<Record<string, Partial<AnalyticsRoutePolicy>>> = {
  '/register': { replay: true },
  '/register/pms': { replay: false },
  '/billing/success': { replay: false },
  '/billing/canceled': { replay: false },
  '/guest-messaging/reviews/:reviewId': { customEvents: false },
  '/messages': { customEvents: false },
  '/settings/integrations/pms/connections/:connectionId': { customEvents: false },
  // One central switch disables the import report detail page entirely.
  '/imports/:orderId/reports': { pageView: false, customEvents: false, replay: false }
};

const ALWAYS_EXCLUDED = new Set([
  '/owner-invitations/:token', '/owner-claims/:token', '/upload/import/:token',
  '/auth/external/callback', '/auth/external/error', '/auth/register/complete',
  '/settings/integrations/pms/connect/ownerrez/callback'
]);

export function resolveAnalyticsRoute(
  state: RouterStateSnapshot,
  role: UserRole | null,
  impersonating: boolean
): AnalyticsRouteDecision {
  const nodes = leaf(state.root).pathFromRoot;
  const routeTemplate = '/' + nodes
    .map((node) => node.routeConfig?.path ?? '')
    .filter((part) => part && part !== '**')
    .join('/');

  const off: AnalyticsRouteDecision = { area: null, routeTemplate: null, policy: { ...ANALYTICS_OFF } };
  if (impersonating || ALWAYS_EXCLUDED.has(routeTemplate)) return off;

  const area: AnalyticsArea | null = REGISTRATION_ROUTES.has(routeTemplate)
    ? 'registration'
    : role === 'owner' && OWNER_ROUTES.has(routeTemplate)
      ? 'owner'
      : null;
  if (!area) return off;
  if (area === 'registration' && routeTemplate !== '/register' && role !== 'owner') return off;

  const dataOverride = nodes.reduce<Partial<AnalyticsRoutePolicy>>((merged, node) => {
    const value = node.routeConfig?.data?.['analytics'] as Partial<AnalyticsRoutePolicy> | undefined;
    return value ? { ...merged, ...value } : merged;
  }, {});
  const policy = {
    ...(area === 'owner' ? OWNER : REGISTRATION),
    ...ROUTE_ANALYTICS_OVERRIDES[routeTemplate],
    ...dataOverride
  };
  if (area === 'owner' || REGISTRATION_REPLAY_EXCLUDED.has(routeTemplate)) policy.replay = false;
  return {
    area,
    routeTemplate,
    policy
  };
}

function leaf(root: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
  let node = root;
  while (node.firstChild) node = node.firstChild;
  return node;
}
